/**
 * Generic phone number validation for international numbers.
 *
 * Replaces the India-only validateIndianPhone/formatIndianPhoneE164 functions
 * while maintaining backward compatibility.
 */

import { COUNTRIES, getCountryByCode, isValidPhoneLength } from './countries';
import type { Country } from './countries';

interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// WhatsApp Business blocks Crimea numbers even though they share Russia's +7
// calling code. Keep the provider restriction at the shared E.164 boundary.
const BLOCKED_WHATSAPP_E164_PREFIXES = ['7978'];

// ============================================
// Client-side validation (with country context)
// ============================================

/**
 * Validate a local phone number for a specific country.
 * Called on the client where the user has selected a country from the dropdown.
 */
export function validatePhone(localNumber: string, countryCode: string): ValidationResult {
  if (!localNumber || typeof localNumber !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  const country = getCountryByCode(countryCode);
  if (!country) {
    return { isValid: false, error: 'Unsupported country' };
  }

  // Remove all non-digit characters
  const digits = localNumber.replace(/\D/g, '');

  if (!digits) {
    return { isValid: false, error: 'Phone number must contain digits' };
  }

  // Check length
  if (!isValidPhoneLength(digits, country)) {
    const expectedLen = Array.isArray(country.phoneLength)
      ? country.phoneLength.join(' or ')
      : country.phoneLength;
    return { isValid: false, error: `Phone number must be ${expectedLen} digits for ${country.name}` };
  }

  // Check startsWith rule (e.g., India: must start with 6-9)
  if (country.startsWith && country.startsWith.length > 0) {
    const firstDigit = parseInt(digits[0]);
    if (!country.startsWith.includes(firstDigit)) {
      return {
        isValid: false,
        error: `${country.name} mobile numbers must start with ${country.startsWith.join(', ')}`,
      };
    }
  }

  return { isValid: true };
}

/**
 * Format a local phone number to E.164 WITHOUT + prefix (for Supabase auth.users storage).
 * e.g., ("9876543210", "91") → "919876543210"
 */
export function formatPhoneE164(localNumber: string, dialCode: string): string | null {
  const digits = localNumber.replace(/\D/g, '');
  if (!digits || !dialCode) return null;
  return `${dialCode}${digits}`;
}

/**
 * Format a local phone number to E.164 WITH + prefix (for phone_otp_verifications storage).
 * e.g., ("9876543210", "91") → "+919876543210"
 */
export function formatPhoneWithPlus(localNumber: string, dialCode: string): string | null {
  const e164 = formatPhoneE164(localNumber, dialCode);
  return e164 ? `+${e164}` : null;
}

// ============================================
// Server-side validation (E.164 string only)
// ============================================

/**
 * Validate a full E.164 phone number (without + prefix, as stored in Supabase).
 * e.g., "919876543210" or "14155551234"
 *
 * Used server-side where the client has already formatted the number.
 * Reverse-looks up the country from dial code and validates length.
 */
export function validateE164(phone: string): ValidationResult {
  if (!phone || typeof phone !== 'string') {
    return { isValid: false, error: 'Phone number is required' };
  }

  const digits = phone.replace(/\D/g, '');

  // E.164 numbers are 7-15 digits (including country code)
  if (digits.length < 7 || digits.length > 15) {
    return { isValid: false, error: 'Invalid phone number format' };
  }

  if (BLOCKED_WHATSAPP_E164_PREFIXES.some((prefix) => digits.startsWith(prefix))) {
    return { isValid: false, error: 'WhatsApp login is unavailable for this region' };
  }

  // Try to match a supported country by dial code (longest match first)
  const parsed = parseE164Phone(digits);
  if (!parsed) {
    return { isValid: false, error: 'Phone number is from an unsupported country' };
  }

  // Validate local number length for matched country
  if (!isValidPhoneLength(parsed.localNumber, parsed.country)) {
    return { isValid: false, error: 'Invalid phone number length for this country' };
  }

  // Check startsWith rule
  if (parsed.country.startsWith && parsed.country.startsWith.length > 0) {
    const firstDigit = parseInt(parsed.localNumber[0]);
    if (!parsed.country.startsWith.includes(firstDigit)) {
      return { isValid: false, error: 'Invalid mobile number for this country' };
    }
  }

  return { isValid: true };
}

// ============================================
// Parsing / Display helpers
// ============================================

interface ParsedPhone {
  country: Country;
  dialCode: string;
  localNumber: string;
}

/**
 * Parse an E.164 number (without +) into country + local number.
 * Tries longest dial code first to handle ambiguity (e.g., "1" vs "1242").
 */
export function parseE164Phone(e164: string): ParsedPhone | null {
  const digits = e164.replace(/\D/g, '');
  if (!digits) return null;

  // Sort countries by dial code length (longest first) to avoid partial matches
  const sorted = [...COUNTRIES].sort((a, b) => b.dialCode.length - a.dialCode.length);

  for (const country of sorted) {
    if (digits.startsWith(country.dialCode)) {
      const localNumber = digits.slice(country.dialCode.length);
      if (localNumber.length > 0) {
        return { country, dialCode: country.dialCode, localNumber };
      }
    }
  }

  return null;
}

/**
 * Format a stored E.164 number for display.
 * e.g., "919876543210" → "+91 98765 43210"
 * e.g., "14155551234" → "+1 4155551234"
 */
export function formatPhoneDisplay(e164: string): string {
  const digits = e164.replace(/\D/g, '');
  if (!digits) return e164;

  const parsed = parseE164Phone(digits);
  if (!parsed) {
    // Unknown country — just add + prefix
    return `+${digits}`;
  }

  // India: +91 XXXXX XXXXX
  if (parsed.dialCode === '91' && parsed.localNumber.length === 10) {
    return `+91 ${parsed.localNumber.slice(0, 5)} ${parsed.localNumber.slice(5)}`;
  }

  // US/Canada: +1 (XXX) XXX-XXXX
  if (parsed.dialCode === '1' && parsed.localNumber.length === 10) {
    return `+1 ${parsed.localNumber.slice(0, 3)} ${parsed.localNumber.slice(3, 6)} ${parsed.localNumber.slice(6)}`;
  }

  // Generic: +{dialCode} {localNumber}
  return `+${parsed.dialCode} ${parsed.localNumber}`;
}
